import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { ArticleBody, SupportBox, useHelpRole } from '../components/Help';
import { ARTICLES, SECTIONS, articleById, search, visibleTo, type HelpArticle } from '../help';
import { homeFor, useAuth } from '../lib/auth';
import { Icon } from '../lib/icons';

/** The help center: every guide, by section, with search, and Stitch support at the end of each. */
export function HelpCenter() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { state } = useAuth();
  const role = useHelpRole();
  const [query, setQuery] = useState('');
  const visible = useMemo(() => ARTICLES.filter((a) => visibleTo(a, role)), [role]);
  const article = id ? articleById(id) : undefined;
  const found = query.trim() ? search(query).filter((a) => visibleTo(a, role)) : null;
  const back = state?.signedIn && state.user ? homeFor(state) : '/sign-in';

  useEffect(() => {
    document.title = `${article ? article.title : 'Help'} · Stitch`;
    window.scrollTo({ top: 0 });
  }, [article]);

  const bySection = SECTIONS.map((s) => ({ section: s, articles: visible.filter((a) => a.section === s) })).filter((g) => g.articles.length);

  return (
    <div className="help-page">
      <header className="help-top">
        <Link to="/help" className="brand help-brand" aria-label="Stitch help center">
          <span className="brand-word">Stitch</span>
          <span className="brand-bar" />
        </Link>
        <span className="help-top-title">Help center</span>
        <label className="help-search">
          <Icon name="search" size={17} />
          <span className="sr-only">Search help</span>
          <input
            type="search"
            className="input"
            placeholder="Search: holidays, desk phone keys, invite…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && found?.[0]) {
                setQuery('');
                navigate(`/help/${found[0].id}`);
              }
            }}
          />
        </label>
        <Link to={back} className="btn secondary small help-back">
          {state?.signedIn ? 'Back to Stitch' : 'Sign in'}
        </Link>
      </header>

      <div className="help-layout">
        <nav className="help-nav" aria-label="Help topics">
          {bySection.map((g) => (
            <div key={g.section} className="help-nav-group">
              <span className="nav-label">{g.section}</span>
              {g.articles.map((a) => (
                <Link key={a.id} to={`/help/${a.id}`} aria-current={a.id === article?.id ? 'page' : undefined}>
                  {a.title}
                </Link>
              ))}
            </div>
          ))}
        </nav>

        <main className="help-main">
          {found ? (
            <Results query={query} found={found} onPick={() => setQuery('')} />
          ) : article && visibleTo(article, role) ? (
            <article className="help-doc">
              <Link to="/help" className="help-all">
                <Icon name="chevLeft" size={16} /> All guides
              </Link>
              <span className="eyebrow">{article.section}</span>
              <h1>{article.title}</h1>
              <p className="help-lede">{article.summary}</p>
              <ArticleBody article={article} />
              <SupportBox />
            </article>
          ) : id ? (
            <div className="help-doc">
              <h1>That guide isn’t here</h1>
              <p className="help-lede">
                It may have moved. <Link to="/help">See every guide</Link>.
              </p>
            </div>
          ) : (
            <div className="help-doc">
              <h1>How can we help?</h1>
              <p className="help-lede">Short guides to everything in Stitch, with pictures of each screen. Every screen also has a Help button at the top that opens its guide.</p>
              <div className="help-cards">
                {bySection.map((g) => (
                  <section key={g.section} className="help-card" aria-labelledby={`sec-${g.section}`}>
                    <h2 id={`sec-${g.section}`}>{g.section}</h2>
                    <ul>
                      {g.articles.map((a) => (
                        <li key={a.id}>
                          <Link to={`/help/${a.id}`}>{a.title}</Link>
                          <span className="muted">{a.summary}</span>
                        </li>
                      ))}
                    </ul>
                  </section>
                ))}
              </div>
              <SupportBox />
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

function Results({ query, found, onPick }: { query: string; found: HelpArticle[]; onPick: () => void }) {
  return (
    <div className="help-doc" role="region" aria-live="polite" aria-label="Search results">
      <Link to="/help" className="help-all" onClick={onPick}>
        <Icon name="chevLeft" size={16} /> All guides
      </Link>
      <h1>{found.length ? `Guides about “${query.trim()}”` : `Nothing about “${query.trim()}” yet`}</h1>
      {found.length ? (
        <ul className="help-results">
          {found.map((a) => (
            <li key={a.id}>
              <Link to={`/help/${a.id}`} onClick={onPick}>
                {a.title}
              </Link>
              <span className="muted">
                {a.section} · {a.summary}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="help-lede">Try other words, or ask Stitch support below.</p>
      )}
      <SupportBox />
    </div>
  );
}
